import { useState } from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';

// local storage saved preferences structure:
// ls(`table-preferences-${saveID}`) = {
//    'tableKey': { preferences }
// }

export const useCarpentr = ({
  selected = [],
  starred = [],
  search = '',
  schema = [],
  filters = [],
  initialData = [],
  currentPage = 1,
  rowsPerPage = 10,
  sortColumn = '',
  sortOrder = 'asc',
  ls,
  saveId, // Usually user uuid
  tableKey = '', // Unique key for this specific table to save preferences
  persistAllPreferences = true, // persist currentPage, search, and filters preferences when table is remounted (recommend false if table can be opened under multiple entities, etc.)
  // If persistAllPreferences = false, at least one of the items below is required:
  entityId, // For use when persistAllPreferences = false when same table is loaded under a different entity
  isFirstRender, // For use when persistAllPreferences = false and page is first rendered (useFirstRender hook)
}) => {
  let $searchKeys = [];
  schema.forEach((d) => {
    if (d.type === 'string' || d.type === 'tags') {
      $searchKeys = [...$searchKeys, d.name];
    }
  });

  const lsTablePreferencesKey = `table-preferences-${saveId}`;

  // SAVE PREFERENCE
  // saves search, sortOrder, sortColumn, currentPage, rowsPerPage, filters
  const savePreference = (setting, value) => {
    if (!ls || !saveId) return;

    const saveIdPreferences = ls(lsTablePreferencesKey) || {}; // ls preferences object (all tables with this saveId)

    // Save setting to local storage
    let tablePreferences = {};
    if (saveIdPreferences[tableKey]) { // if the setting exists, update it
      tablePreferences = {
        ...saveIdPreferences[tableKey],
        [setting]: value,
      };
    } else { // if setting doesn't exist, create it
      tablePreferences[setting] = value;
    }

    const lsPayload = {
      ...saveIdPreferences,
      [tableKey]: tablePreferences,
    };
    ls(lsTablePreferencesKey, lsPayload);
  };

  // Grab table preferences from local storage
  let lsTablePreferences = {};
  if (ls && saveId) {
    if (!tableKey) console.error('Carpentr Error: tableKey not provided');

    const saveIdPreferences = ls(lsTablePreferencesKey);
    if (saveIdPreferences?.[tableKey]) {
      lsTablePreferences = saveIdPreferences[tableKey];

      // Remove ls preferences that should not be persisted
      if (persistAllPreferences === false) {
        if (isFirstRender === undefined && !entityId) {
          console.error(`Carpentr Error (${tableKey} table): Either "isFirstRender" or "entityId" must be used when persistAllPreferences === false.`);
        } else if (isFirstRender || (entityId && entityId !== lsTablePreferences.entityId)) {
          delete lsTablePreferences.currentPage;
          delete lsTablePreferences.search;
          delete lsTablePreferences.filters;
          if (entityId) lsTablePreferences.entityId = entityId;

          const lsPayload = {
            ...saveIdPreferences,
            [tableKey]: lsTablePreferences,
          };
          ls(lsTablePreferencesKey, lsPayload);
        }
      }
    }
  } else if ((ls && !saveId) || (saveId && !ls)) {
    console.error(`Carpentr Error (${tableKey} table): Both "saveId" and "ls" arguments must be provided to enable saving of table preferences in local storage.`);
  }

  let initialSchema = schema;
  // If schema is saved in local storage, reconcile with current schema (if columns configuration has been altered)
  if (lsTablePreferences.schema) {
    initialSchema = schema
      .map((d) => {
        const lsSchemaItem = lsTablePreferences.schema.find((item) => item.name && item.name === d.name);
        if (!lsSchemaItem) return d;
        return {
          ...d,
          isColumnVisible: typeof lsSchemaItem.isColumnVisible === 'boolean' ? lsSchemaItem.isColumnVisible : d.isColumnVisible,
          startingWidth: lsSchemaItem.startingWidth,
        };
      })
      .sort((a, b) => {
        const aIndex = lsTablePreferences.schema.findIndex((item) => item.name && item.name === a.name);
        const bIndex = lsTablePreferences.schema.findIndex((item) => item.name && item.name === b.name);
        if (aIndex === -1 || bIndex === -1) return 0;

        return aIndex > bIndex ? 1 : -1;
      });

    savePreference('schema', initialSchema);
  }

  const [$selected, $setSelected] = useState(selected);
  const [$starred, $setStarred] = useState(starred);
  const [$isAllSelected, $setIsAllSelected] = useState(selected.length === initialData.length);
  const [$search, $setSearch] = useState(lsTablePreferences.search || search);
  const [$filterOptions, $setFilterOptions] = useState([]);
  const [$filters, $setFilters] = useState(lsTablePreferences.filters || filters);
  const [$initialData, $setInitialData] = useState(initialData);
  const [$currentPage, $setCurrentPage] = useState(lsTablePreferences.currentPage || currentPage);
  const [$rowsPerPage, $setRowsPerPage] = useState(lsTablePreferences.rowsPerPage || rowsPerPage);
  const [$sortColumn, $setSortColumn] = useState(lsTablePreferences.sortColumn || sortColumn);
  const [$sortOrder, $setSortOrder] = useState(lsTablePreferences.sortOrder || sortOrder);
  const [$totalPages, $setTotalPages] = useState(Math.ceil(initialData.length / rowsPerPage));
  const [$schema, $setSchema] = useState(initialSchema);
  const [$textWrap, $setTextWrap] = useState(lsTablePreferences.textWrap || []);
  const [$stickyColumn, $setSticky] = useState(lsTablePreferences.stickyColumn || null);

  const setSticky = (d) => {
    $setSticky(d);
    savePreference('stickyColumn', d);
  };

  // Needs to save on click
  const setStarred = (uuid) => {
    const uuidIndex = $starred.indexOf(uuid);
    let tempStarred;
    if (uuidIndex > -1) {
      tempStarred = $starred.filter((d) => d !== uuid);
    } else {
      tempStarred = [...$starred, uuid];
    }
    $setStarred(tempStarred);
  };

  const setTextWrap = (newTextWrapArray) => {
    $setTextWrap(newTextWrapArray);
    savePreference('textWrap', newTextWrapArray);
  };

  // When Updated Schema (incoming tag options from server), reprocess $filterOptions
  useDeepCompareEffect(() => {
    const filterOptions = $schema.filter((d) => d.name && d.isFilter !== false && !d.isOptionColumn);
    $setFilterOptions(filterOptions);
  }, [$schema]);

  // When Data returns from API, this function sets it as default.
  useDeepCompareEffect(() => {
    $setInitialData(initialData);
    if (initialData.length > 0) {
      const totalPages = Math.ceil(initialData.length / $rowsPerPage);
      $setTotalPages(totalPages);
      if ($currentPage > totalPages) {
        $setCurrentPage(totalPages || 1);
      }
    }
  }, [initialData]);

  // FILTERS
  const scrubFilters = (filters) => filters.filter((f) => f.filterValue !== '');

  const setFilters = (filters) => {
    savePreference('filters', filters);
    $setFilters(filters);

    // Reset selected (Decided to remove this even though some items may be selected yet not visible)
    // if (!preventSetSelected) {
    //   $setSelected([]);
    //   $setIsAllSelected(false);
    // }
  };

  const applyFilters = (array, validFilters) => {
    const filtered = array.filter((item) => validFilters.every((filterObj) => {
      // INVALID
      if (filterObj.type.type !== 'tags' && filterObj.operatorValue !== 'is blank' && (item[filterObj.filterKey] === null || item[filterObj.filterKey] === undefined)) {
        return false;
      }

      // STRING
      if (filterObj.type.type === 'string') {
        const value = item[filterObj.filterKey]?.toString().toLowerCase();
        const filterByValue = filterObj.filterValue.toString().toLowerCase();

        switch (filterObj.operatorValue) {
          case 'is':
            if (value.includes(filterByValue)) return true;
            break;
          case 'is not':
            if (!value.includes(filterByValue)) return true;
            break;
          case 'is blank':
            if (!item[filterObj.filterKey] || item[filterObj.filterKey] === ' ') return true;
            break;
          case 'is not blank':
            if (item[filterObj.filterKey] && item[filterObj.filterKey] !== ' ') return true;
            break;
          default:
            return false;
        }
      }

      // NUMBER
      if (filterObj.type.type === 'number') {
        const value = parseInt(item[filterObj.filterKey], 10);
        const filterByValue = parseInt(filterObj.filterValue, 10);

        switch (filterObj.operatorValue) {
          case 'is equal to':
            if (value === filterByValue) return true;
            break;
          case 'is less than':
            if (value < filterByValue) return true;
            break;
          case 'is greater than':
            if (value > filterByValue) return true;
            break;
          case 'is blank':
            if (!item[filterObj.filterKey] || item[filterObj.filterKey] === ' ') return true;
            break;
          case 'is not blank':
            if (item[filterObj.filterKey] && item[filterObj.filterKey] !== ' ') return true;
            break;
          default:
            return false;
        }
      }

      // BOOLEAN
      if (filterObj.type.type === 'boolean') {
        const value = item[filterObj.filterKey];
        const filterByValue = filterObj.filterValue === 'true';

        switch (filterObj.operatorValue) {
          case 'is':
            if (value === filterByValue) return true;
            break;
          case 'is not':
            if (value !== filterByValue) return true;
            break;
          default:
            return false;
        }
      }

      // DATE
      // Do NOT pass in dates formatted with slashes DD/MM/YYYY
      if (filterObj.type.type === 'date') {
        const value = new Date(item[filterObj.filterKey]);
        let filterByValue = new Date(filterObj.filterValue);

        // Dates in timestamptz format
        if (item[filterObj.filterKey]?.includes('T')) {
          const valueTZ = `T${item[filterObj.filterKey].split('T')[1]}`;
          filterByValue = new Date(`${filterObj.filterValue}${valueTZ}`);
        }

        const day1 = value.getDate();
        const month1 = value.getMonth();
        const year1 = value.getFullYear();

        const day2 = filterByValue.getDate();
        const month2 = filterByValue.getMonth();
        const year2 = filterByValue.getFullYear();

        switch (filterObj.operatorValue) {
          case 'before':
            // Check if date is before filter date AND days do not match
            if (value < filterByValue && !(day1 === day2 && month1 === month2 && year1 === year2)) {
              return true;
            }
            break;
          case 'after':
            // Check if date is after filter date AND days do not match
            if (value > filterByValue && !(day1 === day2 && month1 === month2 && year1 === year2)) {
              return true;
            }
            break;
          case 'on':
            if (day1 === day2 && month1 === month2 && year1 === year2) return true;
            break;
          case 'is blank':
            if (!item[filterObj.filterKey]) return true;
            break;
          case 'is not blank':
            if (item[filterObj.filterKey]) return true;
            break;
          default:
            return false;
        }
      }

      // TAGS
      if (filterObj.type.type === 'tags') {
        const rawValue = item[filterObj.filterKey];

        const isBlank =
          rawValue === null ||
          rawValue === undefined ||
          (Array.isArray(rawValue) && rawValue.length === 0);

        const filterByValue = filterObj.filterValue.toString().toLowerCase();

        switch (filterObj.operatorValue) {
          case 'is':
            if (!isBlank) {
              if (Array.isArray(rawValue)) {
                return rawValue.some((tag) => tag.toString().toLowerCase() === filterByValue);
              }
              return rawValue.toString().toLowerCase() === filterByValue;
            }
            break;

          case 'is not':
            if (isBlank) return true;

            if (Array.isArray(rawValue)) {
              return !rawValue.some((tag) => tag.toString().toLowerCase() === filterByValue);
            }

            return rawValue.toString().toLowerCase() !== filterByValue;

          case 'is blank':
            if (isBlank) return true;
            break;

          case 'is not blank':
            if (!isBlank) return true;
            break;

          default:
            return false;
        }
      }

      return false;
    }));

    return filtered;
  };

  // SEARCH
  const setSearchTerm = (e) => {
    savePreference('search', e.target.value);
    $setSearch(e.target.value);

    // Reset selected (Decided to remove this even though some items may be selected yet not visible)
    // $setSelected([]);
    // $setIsAllSelected(false);
  };

  const searchFilter = (arr, searchTerm, searchkeys) => {
    const filteredArray = arr.filter((obj) => searchkeys.some((key) => {
      if (obj[key] === null || obj[key] === undefined) {
        return false;
      }
      return obj[key].toString().toLowerCase()
        .includes(searchTerm.toLowerCase());
    }));

    return filteredArray;
  };

  // SORT
  const sortByColumn = (array) => {
    const order = $sortOrder.toLowerCase();

    return array.slice().sort((a, b) => {
      let x = a[$sortColumn];
      let y = b[$sortColumn];

      if (x === null && y !== null) {
        return 1;
      }
      if (x !== null && y === null) {
        return -1;
      }

      if (typeof x === 'string') {
        x = (`${x}`).toLowerCase();
      }
      if (typeof y === 'string') {
        y = (`${y}`).toLowerCase();
      }

      if (order === 'desc') {
        return ((x < y) ? 1 : ((x > y) ? -1 : 0));
      }
      return ((x < y) ? -1 : ((x > y) ? 1 : 0));
    });
  };

  const setSchema = (array) => {
    $setSchema(array);
    savePreference('schema', array);
  };

  const setColumnSortToggle = (sortColumn, providedSortOrder) => {
    let sortOrder = $sortOrder;
    if (providedSortOrder) {
      sortOrder = providedSortOrder;
    } else {
      if (sortColumn === $sortColumn) {
        sortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        sortOrder = 'asc';
      }
    }
    $setSortColumn(sortColumn);
    $setSortOrder(sortOrder);

    savePreference('sortColumn', sortColumn);
    savePreference('sortOrder', sortOrder);
  };

  // ROWS PER PAGE
  const setRowsPerPage = (value) => {
    let rowsPerPage = value;

    if (typeof rowsPerPage === 'string') {
      rowsPerPage = parseInt(rowsPerPage, 10);
    }

    const totalPages = Math.ceil($initialData.length / rowsPerPage);
    const currentPage = totalPages >= $currentPage ? $currentPage : 1;
    $setRowsPerPage(rowsPerPage);
    $setTotalPages(totalPages);
    $setCurrentPage(currentPage);

    savePreference('rowsPerPage', rowsPerPage);
  };

  // FILTER, SEARCH, AND SORT INITIAL DATA
  let assembledData = $initialData;
  const getVisibleData = () => {
    const offset = ($currentPage - 1) * parseInt($rowsPerPage, 10);
    const topOfRange = offset + parseInt($rowsPerPage, 10);

    // FILTERS
    if ($filters.length > 0) {
      const validFilters = scrubFilters($filters);
      if (validFilters.length > 0) {
        assembledData = applyFilters(assembledData, validFilters);
      }
    }

    // SEARCH
    if ($search) {
      assembledData = searchFilter(assembledData, $search, $searchKeys);
    }

    // RESET PAGE COUNT
    const totalPages = Math.ceil(assembledData.length / $rowsPerPage);
    if (totalPages !== $totalPages) {
      $setTotalPages(totalPages);
      if ($currentPage > totalPages) {
        $setCurrentPage(totalPages || 1);
      }
    }

    // SORT
    if ($sortColumn) {
      assembledData = sortByColumn(assembledData);
    }

    return assembledData.filter((d, i) => i >= offset && i < topOfRange);
  };

  const setCurrentPage = (page) => {
    $setCurrentPage(page);
    savePreference('currentPage', page);
  };

  const setSelected = (uuid) => {
    const uuidIndex = $selected.indexOf(uuid);
    let tempSelected;
    if (uuidIndex > -1) {
      tempSelected = $selected.filter((d) => d !== uuid);
    } else {
      tempSelected = [...$selected, uuid];
    }
    $setSelected(tempSelected);
    $setIsAllSelected(tempSelected.length === assembledData.length);
  };

  const toggleSelectAll = () => {
    let tempSelected;
    let allSelected;
    if ($selected.length !== assembledData.length) {
      tempSelected = assembledData.map((d) => d.uuid);
      allSelected = true;
    } else {
      tempSelected = [];
      allSelected = false;
    }
    $setSelected(tempSelected);
    $setIsAllSelected(allSelected);
  };

  return {
    selected: $selected,
    setSelected,
    isAllSelected: $isAllSelected,
    starred: $starred,
    setStarred,
    toggleSelectAll,
    search: $search,
    schema: $schema,
    setSchema,
    filters: $filters,
    scrubFilters,
    filterOptions: $filterOptions,
    currentPage: $currentPage,
    rowsPerPage: $rowsPerPage,
    sortColumn: $sortColumn,
    sortOrder: $sortOrder,
    totalPages: $totalPages,
    setColumnSortToggle,
    setCurrentPage,
    setRowsPerPage,
    setSearchTerm,
    setFilters,
    nextDisabled: $totalPages === $currentPage,
    prevDisabled: $currentPage === 1,
    visibleData: getVisibleData(),
    assembledDataLength: assembledData.length,
    stickyColumn: $stickyColumn,
    setSticky,
    setSortColumn: $setSortColumn,
    setSortOrder: $setSortOrder,
    setTextWrap,
    textWrap: $textWrap,
  };
};
